import React, { useState, useEffect } from 'react';
import { Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import parse from 'html-react-parser';
import DataTable from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, Link } from 'react-router-dom';
import './Dashboard2.css';
import Sidebar from '../SidebarNew/Sidebar';
import Navbar from '../NavbarProfile/NavbarProfile';
import Fillbutton from '../FillButtonNew/Fillbutton';
import CustomPagination from '../CustomPagination/CustomPagination';
import Instance from '../../Utils/Axios';
import PremiumLoader from '../Loader/PremiumLoader';
import { showToast } from '../../Utils/index';
import { BuyPrModal } from '../Modal/customModal';
import useKeyScroll from "../../Hooks/useKeyScroll";

const TruncatedPackageName = ({ name, id, maxLines = 3, isTitle = false }) => {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [name]);

  const content = (
    <div
      ref={textRef}
      className={isTitle ? "expiry-label mb-0 w-100" : "w-100 m-0 p-0"}
      style={{
        fontWeight: isTitle ? '700' : 'inherit',
        color: isTitle ? '#000' : 'inherit',
        fontSize: isTitle ? '12px' : 'inherit',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        cursor: isTruncated ? 'pointer' : 'default'
      }}
    >
      {name}
    </div>
  );

  return isTruncated ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{name}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};


const Dashboard2 = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useKeyScroll();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState([]);
  const [packageData, setPackageData] = useState(null);
  const [notificationData, setNotificationData] = useState([]);
  const [prListData, setPrListData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  useEffect(() => {
    const fetchDashboardData = () => {
      setListLoading(true);
      if (!packageData) {
        setLoading(true);
      }

      const payload = {
        pageNumber: currentPage,
        pageSize: rowsPerPage,
      };
      if (sortConfig.key) {
        payload.orderBy = sortConfig.key;
        payload.orderByType = sortConfig.direction === "ascending" ? "ASC" : "DESC";
      }

      Instance.post("/user/dashboard", payload)
        .then((res) => {
          const dashMsg = res.data?.message;
          setDashboardData(
            Array.isArray(dashMsg?.package?.data) ? dashMsg.package.data : []
          );
          setPackageData(dashMsg);
          setTotalRecords(dashMsg?.package?.totalRecord || dashMsg?.package?.activePackageCount || 0);
          setNotificationData(dashMsg?.pressRelease?.notification || []);
          setError("");
        })
        .catch((err) => {
          setError("Cannot fetch dashboard data");
          console.error("Failed to fetch dashboard data", err);
        })
        .finally(() => {
          setLoading(false);
          setListLoading(false);
        });
    };

    fetchDashboardData();
  }, [sortConfig, currentPage, rowsPerPage]);

  useEffect(() => {

    const fetchPrListData = () => {
      const token = localStorage.getItem("tokenId");

      Instance.post("/user/pressrelease/list", { /* tokenId: token */ })
        .then((res) => {
          const prListMsg = res.data?.message || [];
          setPrListData(Array.isArray(prListMsg) ? prListMsg : []);
        })
        .catch((err) => {
          console.error("Failed to fetch PR list data", err);
        });
    };

    fetchPrListData();
  }, []);

  // Helper to format date strings
  const formatDate = (dateString, format = 'short') => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString.replace(' ', 'T'));
    if (isNaN(date.getTime())) return dateString;

    if (format === 'custom-bullet') {
      const datePart = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${datePart} • ${timePart}`;
    }

    const options = format === 'long'
      ? { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: 'short', day: '2-digit' };

    return date.toLocaleDateString('en-US', options);
  };

  const getRelativeExpiry = (expiryDateString) => {
    if (!expiryDateString) return "N/A";

    const expiry = new Date(expiryDateString.replace(" ", "T"));
    const now = new Date();

    const diffMs = expiry - now;
    const isExpired = diffMs < 0;

    const absDiff = Math.abs(diffMs);

    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    let text = "";

    if (days < 7) {
      text = `${days} day${days === 1 ? "" : "s"}`;
    } else if (days < 30) {
      text = `${weeks} week${weeks === 1 ? "" : "s"}`;
    } else if (days < 365) {
      text = `${months} month${months === 1 ? "" : "s"}`;
    } else {
      text = `${years} year${years === 1 ? "" : "s"}`;
    }

    if (isExpired) {
      return `expired ${text} ago`;
    } else {
      return `about to expire in ${text}`;
    }
  };

  // Safe access to nested data using packageData (whole message)
  const publishedCount = packageData?.pressRelease?.publishedCount || 0;
  const notificationsList = notificationData || [];
  const activePackageCount = packageData?.package?.activePackageCount || 0;
  const packagesData = dashboardData || [];

  // Process history items from separate PR list (My Last 5 PR)
  const historyItems = prListData.slice(0, 5).map(item => ({
    title: item.title,
    time: formatDate(item.createdOn, 'long')
  }));

  // Process notifications - get latest 3 and map status
  const getStatusInfo = (status) => {
    switch (status) {
      case 'PUBLISHED': return { color: '#4caf50', label: 'Published' };
      case 'REJECT': return { color: '#f3595a', label: 'Rejected' };
      case 'OPEN': return { color: '#ff8132', label: 'Action Required' };
      case 'PENDING': return { color: '#ffc107', label: 'Pending' };
      case 'DELETED': return { color: '#333333', label: 'Deleted' };
      case 'REPORT_UPDATED': return { color: '#b388ff', label: 'Report Updated' };
      default: return { color: '#ffc107', label: 'Pending' };
    }
  };

  const dynamicNotifications = [...notificationsList]
    .sort((a, b) => new Date(b.updatedOn.replace(' ', 'T')) - new Date(a.updatedOn.replace(' ', 'T')))
    .slice(0, 3)
    .map(item => {
      const { color, label } = getStatusInfo(item.status);
      return {
        title: `PR ID: ${item.id} - ${item.status}`,
        desc: item.title,
        description: item.description || '',
        time: formatDate(item.updatedOn, 'long'),
        statusColor: color
      };
    });

  const handleSortChange = (field) => {
    setSortConfig((prev) => ({
      key: field,
      direction:
        prev.key === field && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));

    setCurrentPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={
          sortConfig.direction === "ascending"
            ? faArrowUp
            : faArrowDown
        }
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const columns = React.useMemo(() => [
    {
      name: <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSortChange('name')}>NAME {renderSortIcon('name')}</div>,
      selector: row => row.name,
      width: '250px',
      cell: (row, index) => (
        <TruncatedPackageName name={row.name || "N/A"} id={`tooltip-table-pkg-${index}`} maxLines={2} isTitle={false} />
      ),
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: 'pointer' }} onClick={() => handleSortChange('availablePR')}>CREDITS LEFT {renderSortIcon('availablePR')}</div>,
      selector: row => row.availablePR,
      center: true,
      width: '150px',
    },
    {
      name: <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: 'pointer' }} onClick={() => handleSortChange('expiry')}>EXPIRY {renderSortIcon('expiry')}</div>,
      selector: row => row.expiry,
      center: true,
      width: '130px',
      cell: row => formatDate(row.expiry, 'short'),
    },
    {
      name: 'BUY AGAIN',
      center: true,
      width: '150px',
      cell: row => row.status === "ACTIVE" && row.showOnPricing === "true" ? (
        <button
          className="buy-again-btn"
          onClick={() => purchaseCredits(row.packageId)}
        >
          Buy Again
        </button>
      ) : null,
    },
  ], [sortConfig]);

  const customStyles = {
    table: {
      style: {
        minWidth: 'max-content',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        minHeight: '52px',
        minWidth: 'max-content',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        textTransform: 'uppercase',
        fontWeight: '700',
        color: '#444444',
        flexShrink: 0,
        borderRight: 'none',
      },
    },
    cells: {
      style: {
        color: '#6c757d',
        fontSize: '14px',
        fontWeight: '500',
        whiteSpace: 'normal',
        flexShrink: 0,
        borderRight: 'none',
      },
    },
    rows: {
      style: {
        minHeight: '70px',
        minWidth: 'max-content',
      },
      highlightOnHoverStyle: {
        backgroundColor: '#f5f6ff',
        outline: 'none',
        transition: 'background-color 0.2s ease',
      },
    },
  };

  const sortedDashboardPackages = dashboardData || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const purchaseCredits = (packageId) => {
    if (!packageId) {
      console.error("No packageId provided for purchaseCredits");
      return;
    }

    const token = localStorage.getItem("tokenId");

    Instance.post("/user/credit/buyCredits", {
      // tokenId: token,
      packageId: packageId,
    })
      .then((response) => {
        if (response.data.resCode === "200") {
          const superPNR = response.data.superPNR;
          if (superPNR) {
            localStorage.setItem("superPNR", superPNR);
          }
          const url = `/customer/pricing/${packageId}`;
          window.open(url, "_blank");
        }
      })
      .catch((error) => {
        console.error("Cannot proceed with buying credits", error);
      });
  };

  const handleProtectedSubmit = (e) => {
    e.preventDefault();
    if (activePackageCount === 0) {
      showToast("You don’t have an active credit or package!", "danger", "error");
      setShowBuyModal(true);
    } else {
      navigate("/customer/submit-pr");
    }
  };

  const paginatedPackages = sortedDashboardPackages;

  return (
    <div className="dashboard_container row gx-0">
      <Sidebar activeSide={1} />
      <div className="main col">
        <Navbar name="Dashboard" hasCredits={activePackageCount > 0} />

        <div className="content-wrapper">
          {loading ? (
            <PremiumLoader fullPage={false} text="Preparing your dashboard..." />
          ) : error ? (
            <div className="alert alert-danger m-4" role="alert">
              {error}
            </div>
          ) : (
            <Row className="gx-4">
              {/* Left Section */}
              <Col lg={8}>
                <div className="stats-grid mb-4">
                  <div className="stat-card">
                    <div className="d-flex flex-row flex-sm-column justify-content-between justify-content-sm-start align-items-center align-items-sm-start">
                      <div className="stat-icon icon-blue d-flex justify-content-sm-start mb-0 mb-sm-2">
                        <img src="/build/img/Newspaper.svg" alt="Articles" width="48" height="48" />
                      </div>
                      <div className="text-end text-sm-start">
                        <div className="stat-value">{publishedCount}</div>
                        <div className="stat-label">Articles Published</div>
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="d-flex flex-row flex-sm-column justify-content-between justify-content-sm-start align-items-center align-items-sm-start">
                      <div className="stat-icon icon-purple d-flex justify-content-sm-start mb-0 mb-sm-2">
                        <img src="/build/img/Package.svg" alt="Packages" width="48" height="48" />
                      </div>
                      <div className="text-end text-sm-start">
                        <div className="stat-value">{activePackageCount}</div>
                        <div className="stat-label">Packages Owned</div>
                      </div>
                    </div>
                  </div>
                  <div className="stat-card compact">
                    <div className="expiry-items d-flex flex-column flex-sm-row">
                      {packagesData.slice(0, 2).map((pkg, idx) => (
                        <div key={idx} className="expiry-card px-2 gap-1 d-flex flex-column" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px', width: '100%' }}>
                          <TruncatedPackageName name={pkg.name} id={`tooltip-pkg-${idx}`} isTitle={true} />
                          <div className="expiry-label small text-muted mb-0 w-100 text-nowrap" style={{ fontSize: '12px' }}>package expired on</div>
                          <div className="expiry-date mb-0 w-100 text-nowrap" style={{ fontSize: '12px' }}>{formatDate(pkg.expiry, 'long')}</div>
                          <div className="expiry-status w-100 text-nowrap" style={{ fontSize: '12px' }}>{getRelativeExpiry(pkg.expiry)}</div>
                        </div>
                      ))}
                      {packagesData.length === 0 && (
                        <div className="empty-state-package w-100 d-flex flex-column align-items-center justify-content-center p-3">
                          <p className="mb-2 fw-bold text-center" style={{ fontSize: '13px' }}>Let's buy your first package</p>
                          <button
                            className="buy-now-btn px-4 py-2"
                            onClick={() => {
                              setLoading(true);
                              setTimeout(() => {
                                setLoading(false);
                                navigate("/customer/pricing");
                              }, 1000);
                            }}
                          >
                            Buy Now
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="expiry-footer mt-2 d-flex justify-content-between align-items-center">
                      <span className="fw-medium" style={{ fontSize: '14px' }}>PR Status:</span>
                      <a href="#" className="view-all-link" onClick={(e) => { e.preventDefault(); navigate("/customer/package-details"); }}>View all</a>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="mb-0 h5 fw-bold" >Package Details</h2>
                  <Link to="/customer/package-details" className="view-all-link">View all</Link>
                </div>
                <div className="package-table-container" style={{ minHeight: '450px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div
                    className="table-responsive package-details-table-scroll-wrapper"
                    ref={scrollContainerRef}
                    tabIndex="0"
                    style={{ outline: "none", flexGrow: 1 }}
                  >
                    {listLoading ? (
                      <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                        <PremiumLoader fullPage={false} text="Loading packages..." />
                      </div>
                    ) : paginatedPackages.length === 0 ? (
                      <div className="empty-state-table d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '450px' }}>
                        <img
                          src="/build/img/Package.svg"
                          alt="Package"
                          width="48"
                          height="48"
                          className="mb-3"
                        />
                        <p className="mb-3 fw-bold">
                          Let's buy your first package
                        </p>
                        <button
                          className="buy-now-btn px-4 py-2"
                          onClick={() => {
                            setLoading(true);
                            setTimeout(() => {
                              setLoading(false);
                              navigate("/customer/pricing");
                            }, 1000);
                          }}
                        >
                          Buy Now
                        </button>
                      </div>
                    ) : (
                      <DataTable
                        columns={columns}
                        data={paginatedPackages}
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                        dense={false}
                        className="package-table-react"
                      />
                    )}
                  </div>
                  <div className="pagination-wrapper">
                    <CustomPagination
                      totalItems={totalRecords}
                      rowsPerPage={rowsPerPage}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                    />
                  </div>
                </div>
              </Col>

              {/* Right Section */}
              <Col lg={4}>
                <div className="history-card mb-4 shadow-sm">
                  <h3 className="h6 fw-bold mb-4">My Last 5 PR</h3>
                  <div className="history-list d-flex flex-column">
                    {historyItems.length > 0 ? (
                      historyItems.map((item, index) => (
                        <div
                          key={index}
                          className="history-item d-flex align-items-start"
                          onClick={() => navigate("/customer/pr-manager")}
                          style={{ cursor: "pointer" }}
                        >
                          <div
                            className="item-icon-wrapper d-flex align-items-start justify-content-start"
                            style={{ flexShrink: 0 }}
                          >
                            <img src="/build/img/notify.svg" alt="Notification" width="30" height="30" />
                          </div>
                          <div className="item-content">
                            <h4 className="h6 mb-1 fw-normal lh-base" style={{ fontSize: "14px" }}>
                              {item.title}
                            </h4>
                            <div className="item-time text-muted small">{item.time}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state-package w-100 d-flex flex-column align-items-center justify-content-center p-3">
                        <p
                          className="mb-3 fw-bold text-center"
                          style={{ fontSize: "15px", color: "#091e42" }}
                        >
                          Let’s Submit your First PR
                        </p>

                        <button
                          className="buy-now-btn px-4 py-2"
                          onClick={handleProtectedSubmit}
                        >
                          Submit PR Now
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="notifications-card shadow-sm">
                  <h3 className="h6 fw-bold mb-4 d-flex justify-content-between align-items-center">
                    Notifications <a href="/customer/notification" className="view-all-link small">View all</a>
                  </h3>
                  <div className="notification-list d-flex flex-column gap-3">
                    {dynamicNotifications.length > 0 ? (
                      dynamicNotifications.map((notif, index) => (
                        <OverlayTrigger
                          key={index}
                          placement="left"
                          show={activeTooltipIndex === index}
                          overlay={
                            <Tooltip
                              id={`tooltip-${index}`}
                              className="custom-tooltip"
                              onMouseEnter={() => setActiveTooltipIndex(index)}
                              onMouseLeave={() => setActiveTooltipIndex(null)}
                            >
                              {parse(notif.description)}
                            </Tooltip>
                          }
                        >
                          <div
                            className="notification-item d-flex gap-2"
                            onMouseEnter={() => setActiveTooltipIndex(index)}
                            onMouseLeave={() => setActiveTooltipIndex(null)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="status-dot mt-1" style={{ backgroundColor: notif.statusColor }}></div>
                            <div className="notif-content">
                              <h4 className="h6 mb-1 fw-bold" style={{ fontSize: '14px' }}>{notif.title}</h4>
                              <p className="text-muted mb-1 small">{notif.desc}</p>
                              <div className="notif-time text-muted extra-small">{notif.time}</div>
                            </div>
                          </div>
                        </OverlayTrigger>
                      ))
                    ) : (
                      <div className="empty-state-notif py-3 text-center">
                        <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No notifications yet</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assistance Box – show only for first-time users (no packages & no PRs) */}
                {packagesData.length === 0 && prListData.length === 0 && (
                  <div className="assistance-box mt-4 p-4 shadow-sm text-white" style={{ position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: "absolute", left: "-20px", bottom: "-20px", opacity: 0.2 }}>
                      <img src="/build/img/lines.svg" alt="" width="150" />
                    </div>
                    <h4 className="h6 fw-bold mb-2" style={{ position: 'relative', zIndex: 1 }}>Need help using the product?</h4>
                    <p className="extra-small mb-3" style={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,0.8)' }}>
                      Get detailed information and walkthroughs.
                    </p>
                    <a
                      href="https://docs.zexprwire.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-light btn-sm fw-bold px-4"
                      style={{ borderRadius: '20px', fontSize: '12px', color: '#595fde', position: 'relative', zIndex: 1 }}
                    >
                      Learn More
                    </a>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </div>
      </div>
      <BuyPrModal
        show={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        onClick={() => {
          setShowBuyModal(false);
          navigate("/customer/pricing");
        }}
        title="You don’t have an active credit or package!"
        message="Buy a PR credit and start your PR campaign to reach millions of new users."
        primaryBtn="Buy PR Credit"
        icon="/build/img/Package.svg"
      />
    </div >
  );
};

export default Dashboard2;
